import axios from 'axios';

import { lang } from 'i18n';

// Single axios instance for every admin API call. Base URL comes from .env
// (VITE_API_BASE_URL) so this points at the Deliveroo backend's /api/admin/* routes.
const axiosServices = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api' });

// Attach the stored admin JWT to every outgoing request.
axiosServices.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem('deliveroo_admin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // The API translates its messages (backend middleware/i18n.js) — ask for the portal's own
    // language, not whatever the browser's locale happens to be.
    config.headers['Accept-Language'] = lang;
    return config;
  },
  (error) => Promise.reject(error)
);

// A validation failure answers { error: 'Validation failed', details: [{ field, message }] }, but
// pages only show `error` — so the actual reasons go into it, and `details` is dropped so a page
// that also prints it doesn't repeat them. Other `details` shapes (e.g. the fee upload's
// [{ index, error }]) are left alone for their page to handle.
const withValidationReasons = (data) => {
  const isValidation = Array.isArray(data?.details) && data.details.length > 0 && data.details.every((d) => d && 'field' in d);
  if (!isValidation || typeof data.error !== 'string') return data;
  const reasons = [...new Set(data.details.map((d) => d.message).filter(Boolean))];
  if (!reasons.length) return data;
  return { ...data, details: undefined, error: `${data.error}: ${reasons.join(' · ')}` };
};

// Centralize 401 handling — a stale/expired token should always bounce to /login,
// no matter which page/hook triggered the request.
axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      window.localStorage.removeItem('deliveroo_admin_token');
      window.localStorage.removeItem('deliveroo_admin_user');
      window.location.assign('/login');
    }
    return Promise.reject(withValidationReasons(error.response?.data) || { error: error.message || 'Unknown error' });
  }
);

export default axiosServices;
